import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import type { IconList } from '@snowit/assets';
import type { ButtonEvent } from '@snowit/icetypes/events';
import type { TenantModel } from '@snowit/types/tenants';

import useBooleans from '@snowit/hooks/useBooleans';
import useBreakpoints from '@snowit/hooks/useBreakpoints';
import Portal from '@snowit/hooks/usePortals';
import useAuth from '@snowit/system/useAuth';
import useLanguage from '@snowit/system/useLanguage';
import useTenants from '@snowit/system/useTenants';
import Icon from '@snowit/ui/Icon';
import { Button, ButtonIcon, Flex, Input, Tabs, Typo } from '@snowit/ui/index';
import DropdownHeader from '@snowit/ui/utils/DropdownItems/DropdownHeader';
import { m } from '@snowit/ui/utils/Motion';
import PERMISSIONS from '@snowit/utilities/permissions';
import { sortTenants } from '@snowit/utilities/sorters';

import cx from 'classnames';
import s from './TenantGlobalPicker.module.scss';

const TABS = ['tenants', 'groups'] as const;
type TabKey = (typeof TABS)[number];
const ICON_LIMIT = 4;

const TenantGlobalPicker = () => {
  const { t } = useLanguage();
  const ref = useRef<HTMLButtonElement>(null);
  const [bools, boolsFn] = useBooleans('modal');
  const {
    tenants,
    activeTenants,
    selectTenant,
    setBookmarks,
    bookmarks,
    addGroup,
    removeGroup,
    groups,
    getTenantName,
  } = useTenants();
  const { minWidth } = useBreakpoints();
  const { authType, hasPermission } = useAuth();
  const [input, setInput] = useState<string>('');
  const [tab, setTab] = useState<TabKey>('tenants');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (bools.modal && tab === 'tenants' && inputRef.current) {
      inputRef.current.focus();
    }
  }, [bools.modal, tab]);

  const canSelectAllTenants = hasPermission(PERMISSIONS.SELECTORS__TENANTS_SELECT_ALL);
  const hideTenantSelector =
    authType !== 'ADMIN' && hasPermission(PERMISSIONS.SELECTORS__TENANTS_HIDE);

  const tenantOptions = useMemo((): TenantModel[] => {
    if (hideTenantSelector) return [];
    const all: TenantModel = {
      uid: 'default',
      name: t('lzy.const.tenants_all'),
    } as TenantModel;
    const filtered = tenants
      .filter((tenant) => tenant.name && tenant.uid)
      .filter((tenant) => {
        if (!input) return true;
        return tenant.uid.includes(input) || tenant.name.includes(input);
      })
      .sort((a, b) => {
        const inBookmarks = Number(bookmarks.includes(b.uid)) - Number(bookmarks.includes(a.uid));
        return inBookmarks || sortTenants(a.uid, b.uid);
      });
    const options: TenantModel[] = [...(canSelectAllTenants ? [all] : []), ...filtered];
    return options;
  }, [tenants, authType, canSelectAllTenants, hideTenantSelector, input, tab, bookmarks]);

  const canMakeGroups = authType === 'ADMIN';
  const canMultiSelect = authType === 'ADMIN';
  const canBookmark = authType === 'ADMIN';

  const withoutDefault = useMemo(() => {
    return tenantOptions.filter((it) => it.uid !== 'default');
  }, [tenantOptions]);

  const shouldAutoSelect = useMemo(() => {
    if (authType === 'ADMIN') return false;
    if (canSelectAllTenants) return false;
    if (withoutDefault.length === 1) return true;
    if (withoutDefault.length > 1 && !activeTenants.length) return true;
    return false;
  }, [authType, withoutDefault, activeTenants, canSelectAllTenants]);

  useEffect(() => {
    if (shouldAutoSelect) {
      selectTenant([withoutDefault[0].uid]);
    }
  }, [withoutDefault, shouldAutoSelect]);

  const handleGroup = (uids: string[]) => {
    selectTenant(uids);
    setTab('tenants');
    boolsFn.off('modal');
  };

  const handleBookmark = (event: ButtonEvent, uid: string) => {
    event.stopPropagation();
    if (!canBookmark) return;
    const newTenants = bookmarks.includes(uid)
      ? bookmarks.filter((it) => it !== uid)
      : [...bookmarks, uid];
    setBookmarks(newTenants.sort(sortTenants));
  };

  const handleClick = (tenant: TenantModel) => {
    if (tenant.uid === 'default') {
      selectTenant();
      return;
    }
    if (!canMultiSelect) {
      selectTenant([tenant.uid]);
      return;
    }
    const newTenants = activeTenants.includes(tenant.uid)
      ? activeTenants.filter((it) => it !== tenant.uid)
      : [...activeTenants, tenant.uid];
    selectTenant(newTenants);
  };

  let motionVariants = {};

  if (minWidth.sm) {
    motionVariants = {
      open: { opacity: 1, height: 'auto' },
      collapsed: { opacity: 0, height: 0 },
    };
  } else {
    motionVariants = {
      open: { opacity: 1, translateY: '0%' },
      collapsed: { opacity: 1, translateY: '100%' },
    };
  }

  const renderHeaderDropdown = () => {
    if (minWidth.sm) return null;
    return (
      <DropdownHeader
        classNames={{ wrapper: s.headerDropdown }}
        onClick={() => boolsFn.off('modal')}
      />
    );
  };

  const renderTabs = () => {
    return (
      <div className={s.tabs}>
        <Tabs<TabKey>
          onClick={(_tab) => setTab(_tab.value)}
          tabs={[
            {
              key: 'tenants',
              value: 'tenants',
              active: tab === 'tenants',
              label: t('lzy.label.webshop'),
            },
            {
              key: 'groups',
              value: 'groups',
              active: tab === 'groups',
              label: t('lzy.label.groups'),
              hidden: !canMakeGroups,
            },
          ]}
        />
      </div>
    );
  };

  const renderIcon = (id: string) => (
    <Icon
      key={id}
      className={s.icon}
      name={`icon_${id}` as IconList}
      fallback={'icon_webshop_fallback'}
      sizeV2={1.25}
    />
  );

  const renderIconGroup = (_ids: string[], withLimit?: boolean) => {
    const ids = withLimit ? _ids.slice(0, ICON_LIMIT) : _ids;
    const hasOthers = withLimit && ids.length < _ids.length;
    return (
      <Flex align={'center'} className={s.iconGroup}>
        {ids.map(renderIcon)}
        {hasOthers && (
          <div className={s.others}>
            <Typo
              text={`+${_ids.length - ids.length}`}
              weight={'medium'}
              size={'sm'}
              color={'text'}
            />
          </div>
        )}
      </Flex>
    );
  };

  const renderGroup = (entry: [string, string[]]) => {
    const [id, uids] = entry;
    const sorted = uids.sort(sortTenants);
    return (
      <div className={cx(s.entry, s.group)} key={id} onClick={() => handleGroup(uids)}>
        <Flex direction={'column'} gap={0.5}>
          {renderIconGroup(sorted)}
          <Typo text={sorted.map(getTenantName).join(', ')} size={'sm'} />
        </Flex>

        <ButtonIcon
          icon={'trash'}
          onClick={({ event }) => {
            event.stopPropagation();
            removeGroup(id);
          }}
          theme={'ghost'}
          size={'db'}
          color={'text'}
        />
      </div>
    );
  };

  const renderEntry = (tenant: TenantModel) => {
    const isAll = tenant.uid === 'default';

    return (
      <div
        className={cx(s.entry, { [s.active]: activeTenants.includes(tenant.uid) })}
        key={tenant.uid}
        onClick={() => handleClick(tenant)}
      >
        {renderIcon(tenant.uid)}
        <Flex direction={'column'} gap={0}>
          <Typo text={tenant.name} color={'text'} />
          {!isAll && <Typo text={tenant.uid} size={'sm'} />}
        </Flex>
        {!isAll && canBookmark && (
          <ButtonIcon
            icon={bookmarks.includes(tenant.uid) ? 'bookmark_fill' : 'bookmark'}
            onClick={({ event }) => handleBookmark(event, tenant.uid)}
            theme={'transparent'}
            size={'big'}
            color={bookmarks.includes(tenant.uid) ? 'primary' : 'text'}
          />
        )}
      </div>
    );
  };

  const renderBody = () => {
    if (tab === 'groups') {
      return (
        <Flex className={s.list} direction={'column'} gap={0.25}>
          {Object.entries(groups).map(renderGroup)}
        </Flex>
      );
    }
    return (
      <Fragment>
        <Input
          ref={inputRef}
          classNames={{ wrapInput: s.input }}
          placeholder={'Filter tenant'}
          value={input}
          onChange={({ value }) => setInput(value)}
        />
        <Flex className={s.list} direction={'column'} gap={0.25}>
          {tenantOptions.map(renderEntry)}
        </Flex>
      </Fragment>
    );
  };

  const renderFooter = () => {
    if (!canMakeGroups) return null;
    return (
      <Flex className={s.footer} justify={'flex-end'} align={'center'} gap={1}>
        <Button
          label={t('lzy.action.uncheck')}
          disabled={!activeTenants.length}
          onClick={() => selectTenant()}
          theme={'text'}
          bgColor={'text'}
        />
        {
          <Button
            label={t('lzy.action.make_group')}
            disabled={activeTenants.length < 2}
            onClick={() => addGroup(activeTenants)}
            theme={'text'}
            bgColor={'primary'}
          />
        }
      </Flex>
    );
  };

  const renderContent = () => {
    return (
      <m.div
        className={s.dropdown}
        initial='collapsed'
        animate='open'
        exit='collapsed'
        variants={motionVariants}
        transition={{
          type: 'tween',
          duration: 0.25,
          ease: [0.215, 0.61, 0.355, 1],
        }}
      >
        {renderHeaderDropdown()}
        {renderTabs()}
        <Flex direction={'column'} align={'stretch'} gap={0.5} className={cx(s.body, s[tab])}>
          {renderBody()}
        </Flex>
        {renderFooter()}
      </m.div>
    );
  };

  const renderSelected = () => {
    if (!activeTenants.length) {
      return (
        <Typo
          className={s.placeholder}
          text={t('lzy.const.tenants_all')}
          size={'df'}
          color={'text'}
        />
      );
    }

    if (activeTenants.length === 1) {
      const [_active] = activeTenants;
      return (
        <Flex align={'center'} gap={0.5}>
          {renderIcon(_active)}
          <Typo text={getTenantName(_active)} size={'df'} color={'text'} ellipsis={true} />
        </Flex>
      );
    }

    const actives = activeTenants.sort(sortTenants);
    return renderIconGroup(actives, true);
  };

  const renderInput = () => {
    return (
      <button ref={ref} className={s.btn} type={'button'} onClick={() => boolsFn.on('modal')}>
        {renderSelected()}
        <Icon className={s.arrow} name={'arrow_angle_down'} sizeV2={1} color={'text-l'} />
      </button>
    );
  };

  if (!input && !withoutDefault.length) return null;
  if (hideTenantSelector) return null;

  return (
    <Fragment>
      {renderInput()}
      <Portal
        mounted={!!bools.modal}
        onClose={() => boolsFn.off('modal')}
        content={renderContent}
        bindTo={ref.current}
        id={'tenant-partner-global-picker'}
        fullscreen={!minWidth.sm}
        clickToHide
        escToHide
        position={'adaptive'}
      />
    </Fragment>
  );
};

export default TenantGlobalPicker;
